import React, { useState, useEffect } from 'react';
import { useParams, Link,useNavigate  } from 'react-router-dom';
import dbService from '../../../services/dbService';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { Button, Form, Spinner, ProgressBar, Card, ListGroup, Row, Col } from 'react-bootstrap';
import { ArrowLeft, CloudDownload, Diagram3Fill, QuestionCircleFill } from 'react-bootstrap-icons';
import RiskScoreModal from '../Risk/RiskScoreModal'; // Import the drilldown modal
import PageContainer from '../../layout/PageContainer';

const KYCProfile = () => {
    const navigate = useNavigate(); // Initialize navigate hook

    // --- STATE MANAGEMENT ---
    const { customerId } = useParams();
    const [customer, setCustomer] = useState(null);
    const [riskModel, setRiskModel] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState(null);
    const [showRiskModal, setShowRiskModal] = useState(false);
    const { user } = useAuth();
    const isAdmin = user?.role === 'L3 Admin';

    // --- DATA FETCHING ---
    const fetchData = async () => {
        const [customerData, modelData] = await Promise.all([
            dbService.getById('customers', customerId),
            dbService.getById('risk_models', 'customer_risk_model'),
        ]);
        setCustomer(customerData);
        setFormData(customerData);
        setRiskModel(modelData);
    };
    useEffect(() => { fetchData(); }, [customerId]);

    // --- HANDLERS ---
    const handleSync = () => {
        toast.info("Simulating sync with Core Banking System... Data refreshed.");
        dbService.addLog({ user: user.name, action: 'KYC Sync', details: `Synced data for ${customerId}` });
        fetchData();
    };

    const handleSave = async () => {
        await dbService.update('customers', formData);
        await dbService.addLog({ user: user.name, action: 'KYC Update', details: `Updated custom fields for ${customerId}` });
        toast.success("KYC Profile updated.");
        setIsEditing(false);
        fetchData();
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, customFields: { ...prev.customFields, [name]: value } }));
    };

    if (!customer || !riskModel) return <div className="text-center p-5"><Spinner animation="border" /></div>;

    // --- RENDER LOGIC ---
    const scoreVariant = customer.riskBreakdown.total > 75 ? 'danger' : customer.riskBreakdown.total > 40 ? 'warning' : 'success';

    return (
        <PageContainer>
            <Link to="/case-management" className="btn btn-dark mb-3"><ArrowLeft className="me-2" />Back</Link>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>KYC Profile: {customer.fullName}</h2>
                 <Button variant="primary" className="me-2" onClick={() => navigate(`/link-analysis/${customer.id}`)}><Diagram3Fill className="me-2" />Analyze Links</Button>
                <Button variant="outline-primary" onClick={handleSync}><CloudDownload className="me-2" />Sync from CBS</Button>
            </div>
            
            {/* DYNAMIC RISK SCORE CARD */}
            <Card className="mb-4">
                <Card.Header as="h4">Customer Risk Overview</Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={8}>
                            <h5 className="mb-2">Overall Dynamic Risk Score: {customer.riskBreakdown.total} / 100</h5>
                            <ProgressBar now={customer.riskBreakdown.total} variant={scoreVariant} animated />
                            <p className="text-muted mt-2">This is a combined score calculated from the customer's static profile and their dynamic transaction history.</p>
                        </Col>
                        <Col md={4}>
                            <ListGroup variant="flush">
                                <ListGroup.Item action onClick={() => setShowRiskModal(true)} className="d-flex justify-content-between align-items-center">
                                    Static KYC Score
                                    <span className="badge bg-secondary rounded-pill fs-6">{customer.riskBreakdown.kycScore}</span>
                                </ListGroup.Item>
                                <ListGroup.Item action onClick={() => setShowRiskModal(true)} className="d-flex justify-content-between align-items-center">
                                    Dynamic Transaction Score
                                    <span className="badge bg-info rounded-pill fs-6">{customer.riskBreakdown.txnScore}</span>
                                </ListGroup.Item>
                                <ListGroup.Item action onClick={() => setShowRiskModal(true)} className="text-primary text-center">
                                    <QuestionCircleFill className="me-2" />
                                    Click for Detailed Breakdown
                                </ListGroup.Item>
                            </ListGroup>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* STATIC CUSTOMER DETAILS CARD */}
            <div className="card">
                <div className="card-header"><h4>Customer Details (<code>{customer.id}</code>)</h4></div>
                <div className="card-body">
                    <Row>
                        <Col md={6}>
                            <p><strong>Date of Birth:</strong> {customer.dateOfBirth || 'N/A'}</p>
                            <p><strong>Address:</strong> {customer.address}</p>
                            <p><strong>Nationality:</strong> {customer.nationality}</p>
                        </Col>
                        <Col md={6}>
                            <p><strong>Occupation:</strong> {customer.occupation}</p>
                            <p><strong>Onboarding Date:</strong> {customer.onboardingDate}</p>
                            <p><strong>Static Risk Rating:</strong> <span className="badge text-bg-secondary">{customer.riskRating}</span></p>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* CUSTOMIZABLE TAGS CARD */}
            <div className="card mt-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h4>Customizable Fields & Tags</h4>
                    {isAdmin && !isEditing && <Button variant="secondary" size="sm" onClick={() => setIsEditing(true)}>Edit</Button>}
                </div>
                <div className="card-body">
                    {isEditing ? (
                        <Form>
                            <Form.Group className="mb-3"><Form.Label>Watchlist Tag</Form.Label><Form.Control type="text" name="watchlistTag" defaultValue={formData.customFields.watchlistTag} onChange={handleFormChange} /></Form.Group>
                            <Form.Group className="mb-3"><Form.Label>Politically Exposed Person (PEP)</Form.Label><Form.Select name="politicallyExposedPerson" defaultValue={formData.customFields.politicallyExposedPerson} onChange={handleFormChange}>
                                <option>No</option><option>Yes - Tier 1</option><option>Yes - Tier 2</option>
                            </Form.Select></Form.Group>
                            <Button variant="primary" onClick={handleSave}>Save</Button>
                            <Button variant="light" className="ms-2" onClick={() => setIsEditing(false)}>Cancel</Button>
                        </Form>
                    ) : (
                        <div>
                            <p><strong>Watchlist Tag:</strong> {customer.customFields.watchlistTag}</p>
                            <p><strong>Politically Exposed Person:</strong> {customer.customFields.politicallyExposedPerson}</p>
                        </div>
                    )}
                </div>
            </div>            
            <RiskScoreModal 
                show={showRiskModal} 
                onHide={() => setShowRiskModal(false)} 
                customer={customer}
                riskModel={riskModel}
            />
        </PageContainer>
    );
};

export default KYCProfile;